/** 
 * Check if two arrays or sets contain the same elements, regardless of order.
 * @param {Array|Set} listA - First array or set to compare.
 * @param {Array|Set} listB - Second array or set to compare.
 * @param {Function} [getKey] - Optional function to extract a key/id from list elements.
 * @returns {boolean} - True if both lists contain the same elements, false otherwise.
 * @example
 * isEquivalent([1, 2, 3], [3, 2, 1]); // returns true
 * isEquivalent(new Set(['a', 'b']), new Set(['b', 'a'])); // returns true
 * isEquivalent([{id: 1}, {id: 2}], [2, 1], x => x.id); // returns true
 */
export function isEquivalent(listA, listB, getKey = (x) => x) {
  const keysA = new Set(Array.from(listA, getKey));
  const keysB = new Set(Array.from(listB, getKey));

  if (keysA.size !== keysB.size) {
    return false;
  }

  // check for compatibility
  return Set.prototype.symmetricDifference
    ? !keysA.symmetricDifference(keysB).size
    : [...keysA].every((key) => keysB.has(key));
}

/**
 * Get the difference between two arrays or sets, regardless of how they're sorted.
 * @param {Array|Set} listA - The array or set to subtract from.
 * @param {Array|Set} listB - The array or set to subtract.
 * @param {Function} [getKey] - Optional function to extract a key/id from list elements.
 * @returns {Set} - A set containing elements in listA that are not in listB.
 * @example
 * difference([1, 2, 3, 4], [2, 4]); // returns Set {1, 3}
 * difference(new Set(['a', 'b', 'c']), new Set(['b'])); // returns Set {'a', 'c'}
 * difference([{id: 1}, {id: 2}, {id: 3}], [2, 3], x => x.id); // returns Set {1}
 */
export function difference(listA, listB, getKey = (x) => x) {
  const keysA = new Set(Array.from(listA, getKey));
  const keysB = new Set(Array.from(listB, getKey));

  // check for compatibility
  return Set.prototype.difference
    ? keysA.difference(keysB)
    : new Set([...keysA].filter((key) => !keysB.has(key)));
}

/**
 * Check if one array or set contains all elements of another, regardless of order.
 * @param {Array|Set} listA - The array or set to check as superset.
 * @param {Array|Set} listB - The array or set to check as subset.
 * @param {Function} [getKey] - Optional function to extract a key/id from list elements.
 * @returns {boolean} - True if listA contains all elements of listB, false otherwise.
 * @example
 * isSuperset([1, 2, 3, 4], [2, 3]); // returns true
 * isSuperset(new Set(['a', 'b', 'c']), ['a', 'd']); // returns false
 * isSuperset(["hello", "world"], ["WORLD"], str => str.toLowerCase()); // returns true
 */
export function isSuperset(listA, listB, getKey = (x) => x) {
  const keysA = new Set(Array.from(listA, getKey));
  const keysB = new Set(Array.from(listB, getKey));

  // check for compatibility
  return Set.prototype.symmetricDifference
    ? keysA.isSupersetOf(keysB)
    : [...keysB].every((key) => keysA.has(key));
}

/**
 * Check if an array or set contains all specified values.
 * @param {Array|Set} collection - The array or set to check.
 * @param {...*} values - Values to look for in the collection.
 * @returns {boolean} - True if the collection contains all specified values, false otherwise.
 * @example
 * contains([1, 2, 3, 4], 2, 3); // returns true
 * contains(new Set(['a', 'b', 'c']), 'a', 'd'); // returns false
 */
export function contains(collection, ...values) {
  return isSuperset(collection, values);
}

/**
 * Get a comparer function for sorting arrays based on a specified index.
 * @param {any => any} indexSelector - Function to extract a comparable value from list elements.
 * @returns {number} A comparer function that can be used with Array.prototype.sort.
 * @example array.sort(getComparer()); // sorts by the elements themselves
 * @example array.sort(getComparer(x => x.customIndex)); // sorts by the customIndex property, but only if it exists on both objects
 */
export function getComparer(indexSelector = (x) => x) {
  return function(a, b) {
    const indexA = indexSelector(a);
    const indexB = indexSelector(b);

    if (indexA != null && indexB != null) {
      return !isNaN(indexA) && !isNaN(indexB)
        ? Number(indexA) - Number(indexB)
        : String(indexA).localeCompare(String(indexB));
    }

    if (indexA == null) { return -1; }
    if (indexB == null) { return 1; }
    return 0;
  };
}